import styled from 'styled-components';
import theme from 'styled-theming';

const backgroundColor = theme('name', {
  light: '#f8f9fa',
  dark: '#1a1a1a',
});

const cardBackgroundColor = theme('name', {
  light: '#ffffff',
  dark: '#2d2d2d',
});

const borderColor = theme('name', {
  light: '#e1e5e9',
  dark: '#404040',
});

const textColor = theme('name', {
  light: '#333333',
  dark: '#ffffff',
});

const hoverColor = theme('name', {
  light: '#f0f2f5',
  dark: '#3a3a3a',
});

export const LandingPageContainer = styled.div`
  min-height: 100vh;
  background-color: ${backgroundColor};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;

  .header-section {
    text-align: center;
    margin-bottom: 60px;

    h1 {
      color: ${textColor};
      margin-bottom: 16px;
    }

    p {
      color: ${textColor};
      opacity: 0.7;
      font-size: 18px;
    }
  }

  .main-tiles-container {
    display: flex;
    align-items: stretch;
    gap: 40px;
    max-width: 1400px;
    width: 100%;
    margin-bottom: 40px;

    @media (max-width: 1024px) {
      flex-wrap: wrap;
      max-width: 900px;
    }

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 24px;
    }
  }

  .portfolios-section {
    width: 100%;
    max-width: 1200px;
    animation: fadeInUp 0.6s ease-out;

    .section-header {
      text-align: center;
      margin-bottom: 40px;

      h2 {
        color: ${textColor};
        margin-bottom: 8px;
      }

      p {
        color: ${textColor};
        opacity: 0.6;
        font-size: 16px;
        margin: 0;
      }
    }

    .loading-container,
    .error-container {
      text-align: center;
      padding: 40px 20px;

      p {
        color: ${textColor};
        font-size: 16px;
        margin: 0;
      }
    }

    .error-container p {
      color: #dc3545;
    }

    .portfolios-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
      gap: 24px;

      @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 16px;
      }
    }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const TileCard = styled.div`
  flex: 1;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;

  .tile-card {
    flex: 1;
    height: 100%;
    border: 2px solid ${borderColor};
    background-color: ${cardBackgroundColor};
    transition: all 0.3s ease;
    border-radius: 12px;

    /* Override Shell DS CardBody (the direct child div) so it doesn't add
       extra padding and fills the card height properly */
    > div {
      padding: 0 !important;
      display: flex !important;
      flex-direction: column !important;
      height: 100%;
    }

    &:hover {
      border-color: #007bff;
      background-color: ${hoverColor};
      transform: translateY(-4px);
      box-shadow: 0 12px 35px rgba(0, 123, 255, 0.15);
    }

    &.main-tile {
      &:hover {
        box-shadow: 0 16px 40px rgba(0, 123, 255, 0.2);
      }
    }

    &.portfolio-card {
      &:hover {
        box-shadow: 0 8px 25px rgba(0, 123, 255, 0.12);
      }
    }
  }

  &.selected .tile-card {
    border-color: #28a745;
    background-color: rgba(40, 167, 69, 0.05);
    box-shadow: 0 8px 25px rgba(40, 167, 69, 0.2);
  }
`;

export const TileContent = styled.div`
  display: flex;
  align-items: flex-start;
  height: 100%;
  padding: 28px 24px;
  gap: 24px;

  .icon-section {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 70px;

    .genesis-icon,
    .eos-icon,
    .new-genesis-icon,
    .portfolio-icon {
      width: 70px;
      height: 70px;
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      font-size: 20px;
      color: white;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    }

    .genesis-icon {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      font-size: 24px;
    }

    .eos-icon {
      background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
      font-size: 16px;
    }

    .new-genesis-icon {
      background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
      font-size: 16px;
    }

    .portfolio-icon {
      background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
      font-size: 18px;
    }
  }

  .content-section {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;

    h3 {
      color: ${textColor};
      margin-bottom: 12px;
      font-weight: 600;
    }

    h4 {
      color: ${textColor};
      margin-bottom: 8px;
      font-weight: 600;
      font-size: 16px;
    }

    p {
      color: ${textColor};
      opacity: 0.7;
      font-size: 14px;
      line-height: 1.6;
      margin: 0 0 12px 0;
    }

    .selected-indicator {
      color: #28a745;
      font-weight: 600;
      font-size: 13px;
      margin-top: 8px;
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }

  .arrow-section {
    color: #007bff;
    opacity: 0.6;
    transition: all 0.3s ease;
    margin-top: auto;
  }

  &:hover .arrow-section {
    opacity: 1;
    transform: translateX(6px);
  }
`;
